import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";

interface authState {
  otpUserData: any;
  otpVerifyUsersData: any;
  userProfile: any;
  passwordType: String | null;
  token: any;
  // Apply-now journey: guestToken from the first api/v1/user-lead/create
  // response. Sent as Authorization on every API call until bypass-login
  // establishes the real session (setUserAuth clears it).
  guestToken: string | null;
  qrcToken: string | null;
  qrcFlowType: "borrower" | "nonBorrower" | null;
  qrcLan: string | null;
  qrcLanOptions: string[];
  // appId values from qrc/user-verification's application bucket — sent as applicationId in the
  // ticket-listing payload (see ViewTickets). Captured pre-login, where that call happens once
  // at OTP time and its response is not available to the listing screen.
  qrcAppIds: string[];
  qrcEmail: string | null;
  qrcName: string | null;
  // Set when a "No, I don't" (non-borrower) verification turned out to belong to an existing
  // customer and the session was upgraded to a borrower one — the Contact Us hub tells them so.
  qrcUpgradedToBorrower: boolean;
  requestSignature: string | null;
  triggerData: any;
  authStatus: boolean;
  permission: any;
  businessType: Array<object> | null;
  loanCategory: Object | null;
  setPasswordKey: string | null;
  SetPasswordArray: Array<any> | null;
  // resetToken from api/v1/user/otp-verify — required by api/v1/user/set-password.
  resetToken: string | null;
  // akSessionRef (formerly bypassToken) from api/v1/user/otp-verify or
  // api/v1/co-applicant/coapplicant-otp-verify — sent only on
  // api/v1/user-lead/bypass-login as its Authorization header.
  bypassToken: string | null;
}

const initialState: authState = {
  otpUserData: null,
  otpVerifyUsersData: null,
  userProfile: null,
  passwordType: null,
  token: null,
  guestToken:
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem("guestToken") : null,
  qrcToken: null,
  qrcFlowType: null,
  qrcLan: null,
  qrcLanOptions: [],
  qrcAppIds: [],
  qrcEmail: null,
  qrcName: null,
  qrcUpgradedToBorrower: false,
  requestSignature: null,
  triggerData: null,
  authStatus: false,
  permission: null,
  businessType: null,
  loanCategory: null,
  setPasswordKey: null,
  SetPasswordArray: null,
  resetToken: null,
  bypassToken: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setOtpUserData(state, action) {
      state.otpUserData = action.payload;
    },
    setOtpVerifyUsersData(state, action) {
      state.otpVerifyUsersData = action.payload;
    },
    setUserProfileData(state, action) {
      state.userProfile = action.payload;
    },
    setPasswordType(state, action) {
      state.passwordType = action.payload;
    },
    setSetPasswordKey(state, action) {
      state.setPasswordKey = action.payload;
    },
    setSetPasswordArray(state, action) {
      state.SetPasswordArray = action.payload;
    },
    setResetToken(state, action) {
      state.resetToken = action.payload || null;
    },
    setBypassToken(state, action) {
      state.bypassToken = action.payload || null;
    },
    setUserAuth(state, action) {
      state.token = action.payload;
      state.authStatus = !!action.payload;
      // Sync with sessionStorage for consistency
      if (action.payload) {
        sessionStorage.setItem('token', action.payload);
        // Real session (e.g. bypass-login) replaces the apply-now guest token.
        state.guestToken = null;
        sessionStorage.removeItem('guestToken');
        // A real login supersedes any pre-login QRC session in this tab. Leaving it behind made
        // the pre-login Contact Us shell (hero, BACK/LOGOUT pills, "Yes, I do / No, I don't"
        // treatment) render for logged-in users, since those screens key off qrcFlowType.
        // Mirrors completeVerification, where a QRC verification clears the login session.
        state.qrcToken = null;
        state.qrcFlowType = null;
        state.qrcLan = null;
        state.qrcLanOptions = [];
        state.qrcAppIds = [];
        state.qrcEmail = null;
        state.qrcName = null;
        state.qrcUpgradedToBorrower = false;
        sessionStorage.removeItem('qrcToken');
        sessionStorage.removeItem('qrcFlowType');
        sessionStorage.removeItem('qrcLan');
        sessionStorage.removeItem('qrcLanOptions');
        sessionStorage.removeItem('qrcAppIds');
        sessionStorage.removeItem('qrcEmail');
        sessionStorage.removeItem('qrcName');
        sessionStorage.removeItem('qrcUpgradedToBorrower');
      } else {
        sessionStorage.removeItem('token');
      }
    },
    setGuestToken(state, action) {
      state.guestToken = action.payload || null;
      if (action.payload) {
        sessionStorage.setItem('guestToken', action.payload);
      } else {
        sessionStorage.removeItem('guestToken');
      }
    },
    setQrcToken(state, action) {
      state.qrcToken = action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcToken', action.payload);
      } else {
        sessionStorage.removeItem('qrcToken');
      }
    },
    clearQrcToken(state) {
      state.qrcToken = null;
      state.qrcFlowType = null;
      state.qrcLan = null;
      state.qrcLanOptions = [];
      state.qrcAppIds = [];
      state.qrcEmail = null;
      state.qrcName = null;
      state.qrcUpgradedToBorrower = false;
      sessionStorage.removeItem('qrcToken');
      sessionStorage.removeItem('qrcFlowType');
      sessionStorage.removeItem('qrcLan');
      sessionStorage.removeItem('qrcLanOptions');
      sessionStorage.removeItem('qrcAppIds');
      sessionStorage.removeItem('qrcEmail');
      sessionStorage.removeItem('qrcName');
      sessionStorage.removeItem('qrcUpgradedToBorrower');
    },
    setQrcFlowType(state, action) {
      state.qrcFlowType = action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcFlowType', action.payload);
      } else {
        sessionStorage.removeItem('qrcFlowType');
      }
    },
    setQrcLan(state, action) {
      state.qrcLan = action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcLan', action.payload);
      } else {
        sessionStorage.removeItem('qrcLan');
      }
    },
    setQrcLanOptions(state, action) {
      state.qrcLanOptions = action.payload ?? [];
      if (state.qrcLanOptions.length) {
        sessionStorage.setItem('qrcLanOptions', JSON.stringify(state.qrcLanOptions));
      } else {
        sessionStorage.removeItem('qrcLanOptions');
      }
    },
    setQrcAppIds(state, action) {
      state.qrcAppIds = action.payload ?? [];
      if (state.qrcAppIds.length) {
        sessionStorage.setItem('qrcAppIds', JSON.stringify(state.qrcAppIds));
      } else {
        sessionStorage.removeItem('qrcAppIds');
      }
    },
    setQrcEmail(state, action) {
      state.qrcEmail = action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcEmail', action.payload);
      } else {
        sessionStorage.removeItem('qrcEmail');
      }
    },
    setQrcName(state, action) {
      state.qrcName = action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcName', action.payload);
      } else {
        sessionStorage.removeItem('qrcName');
      }
    },
    setQrcUpgradedToBorrower(state, action) {
      state.qrcUpgradedToBorrower = !!action.payload;
      if (action.payload) {
        sessionStorage.setItem('qrcUpgradedToBorrower', 'true');
      } else {
        sessionStorage.removeItem('qrcUpgradedToBorrower');
      }
    },
    initializeQrcFromSessionStorage(state) {
      const sessionQrcToken = sessionStorage.getItem('qrcToken');
      if (sessionQrcToken && !state.qrcToken) {
        state.qrcToken = sessionQrcToken;
      }
      const sessionQrcFlowType = sessionStorage.getItem('qrcFlowType');
      if (sessionQrcFlowType && !state.qrcFlowType) {
        state.qrcFlowType = sessionQrcFlowType as "borrower" | "nonBorrower";
      }
      const sessionQrcLan = sessionStorage.getItem('qrcLan');
      if (sessionQrcLan && !state.qrcLan) {
        state.qrcLan = sessionQrcLan;
      }
      const sessionQrcLanOptions = sessionStorage.getItem('qrcLanOptions');
      if (sessionQrcLanOptions && !state.qrcLanOptions.length) {
        try {
          state.qrcLanOptions = JSON.parse(sessionQrcLanOptions);
        } catch {
          state.qrcLanOptions = [];
        }
      }
      const sessionQrcAppIds = sessionStorage.getItem('qrcAppIds');
      if (sessionQrcAppIds && !state.qrcAppIds.length) {
        try {
          state.qrcAppIds = JSON.parse(sessionQrcAppIds);
        } catch {
          state.qrcAppIds = [];
        }
      }
      const sessionQrcEmail = sessionStorage.getItem('qrcEmail');
      if (sessionQrcEmail && !state.qrcEmail) {
        state.qrcEmail = sessionQrcEmail;
      }
      const sessionQrcName = sessionStorage.getItem('qrcName');
      if (sessionQrcName && !state.qrcName) {
        state.qrcName = sessionQrcName;
      }
      if (sessionStorage.getItem('qrcUpgradedToBorrower') === 'true') {
        state.qrcUpgradedToBorrower = true;
      }
    },
    setRequestSignature(state, action) {
      state.requestSignature = action.payload;
    },
    setUserPermission(state, action) {
      state.permission = action.payload;
    },
    setUserBusinessType(state, action) {
      state.businessType = action.payload;
    },
    setUserLoanCategory(state, action) {
      state.loanCategory = action.payload;
    },
    setTriggerData(state, action) {
      state.triggerData = action.payload;
    },
    clearOtpUserData(state) {
      state.otpUserData = null;
      state.otpVerifyUsersData = null;
      state.setPasswordKey = null;
      state.SetPasswordArray = null;
      state.resetToken = null;
    },
    clearPasswordType(state) {
      state.passwordType = null;
    },
    clearAuth(state) {
      state.otpUserData = null;
      state.otpVerifyUsersData = null;
      state.userProfile = null;
      state.token = null;
      state.requestSignature = null;
      state.triggerData = null;
      state.authStatus = false;
      state.permission = null;
      state.businessType = null;
      state.loanCategory = null;
      state.setPasswordKey = null;
      state.SetPasswordArray = null;
      state.resetToken = null;
      state.bypassToken = null;
      state.guestToken = null;
      // Clear sessionStorage as well
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('guestToken');
    },
    // Initialize auth state from sessionStorage if Redux persist hasn't loaded yet
    initializeFromSessionStorage(state) {
      const sessionToken = sessionStorage.getItem('token');
      if (sessionToken && !state.token) {
        state.token = sessionToken;
        state.authStatus = true;
      }
    },
  },
});

export const {
  setGuestToken,
  setOtpUserData,
  setOtpVerifyUsersData,
  setUserProfileData,
  setUserAuth,
  setQrcToken,
  clearQrcToken,
  setQrcFlowType,
  setQrcLan,
  setQrcLanOptions,
  setQrcAppIds,
  setQrcEmail,
  setQrcName,
  setQrcUpgradedToBorrower,
  initializeQrcFromSessionStorage,
  setRequestSignature,
  setPasswordType,
  setSetPasswordKey,
  setSetPasswordArray,
  setResetToken,
  setBypassToken,
  setUserPermission,
  setUserBusinessType,
  setUserLoanCategory,
  setTriggerData,
  clearAuth,
  clearOtpUserData,
  clearPasswordType,
  initializeFromSessionStorage,
} = authSlice.actions;
export const authSelector = (state: RootState) => state?.auth;
export const authStatusSelector = (state: RootState) => state?.auth.authStatus;
export const authTokenSelector = (state: RootState) => state?.auth.token;
export const qrcTokenSelector = (state: RootState) => state?.auth.qrcToken;
export const qrcVerifiedSelector = (state: RootState) => !!state?.auth.qrcToken;
export const qrcFlowTypeSelector = (state: RootState) => state?.auth.qrcFlowType;
export const qrcLanSelector = (state: RootState) => state?.auth.qrcLan;
export const qrcLanOptionsSelector = (state: RootState) => state?.auth.qrcLanOptions;
export const qrcAppIdsSelector = (state: RootState) => state?.auth.qrcAppIds;
export const qrcEmailSelector = (state: RootState) => state?.auth.qrcEmail;
export const qrcNameSelector = (state: RootState) => state?.auth.qrcName;
export const qrcUpgradedToBorrowerSelector = (state: RootState) => state?.auth.qrcUpgradedToBorrower;
export const requestSignatureSelector = (state: RootState) =>
  state?.auth?.requestSignature;
export const userPermissionSelector = (state: RootState) =>
  state?.auth.permission || "";
export const userSelector = (state: RootState) => state?.auth.userProfile;
export const userBusinessSelector = (state: RootState) =>
  state?.auth.businessType;
export const userLoanCatSelector = (state: RootState) =>
  state?.auth?.loanCategory;
export const triggerDataSelector = (state: RootState) => state?.auth?.triggerData;
export default authSlice.reducer;